import {ReactNode} from "react";
import {OtsAvatar} from "./OtsAvatar";

export type OtsMessageListItem = {
  id: string;
  body: ReactNode;
  author?: ReactNode;
  authorName?: string;
  timestamp?: ReactNode;
  metadata?: ReactNode;
};

export type OtsMessageListProps = {
  items: OtsMessageListItem[];
  ariaLabel: string;
  loading?: boolean;
  density?: "compact" | "default";
  loadingState?: ReactNode;
  emptyState?: ReactNode;
  className?: string;
};

/** Theme-aware message/comment history shared by entity profiles and operational views. */
export const OtsMessageList = ({
  items,
  ariaLabel,
  loading = false,
  density = "default",
  loadingState = "Loading messages…",
  emptyState = "No messages yet.",
  className,
}: OtsMessageListProps) => {
  if (loading) {
    return <p role="status" className={["text-sm text-[var(--ots-text-muted)]", className].filter(Boolean).join(" ")}>{loadingState}</p>;
  }

  if (!items.length) {
    return <div className={[
      "rounded-md border border-dashed border-[var(--ots-outline)] bg-[var(--ots-background)] text-center text-sm text-[var(--ots-text-muted)]",
      density === "compact" ? "px-4 py-7" : "px-5 py-10",
      className,
    ].filter(Boolean).join(" ")}>{emptyState}</div>;
  }

  return <ol
    aria-label={ariaLabel}
    data-density={density}
    className={[
      "divide-y divide-[var(--ots-outline-variant)] border-y border-[var(--ots-outline-variant)]",
      className,
    ].filter(Boolean).join(" ")}>
    {items.map(item => <li key={item.id} className={["flex", density === "compact" ? "gap-2.5 py-3" : "gap-3 py-5"].join(" ")}>
      <OtsAvatar name={item.authorName || "OTS user"} size={density === "compact" ? "sm" : "md"} decorative/>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="text-sm font-semibold text-[var(--ots-secondary)]">{item.author ?? item.authorName ?? "OTS user"}</p>
          {item.timestamp != null && <p className="text-xs text-[var(--ots-text-muted)]">{item.timestamp}</p>}
          {item.metadata != null && <div className="flex flex-wrap items-center gap-1.5">{item.metadata}</div>}
        </div>
        <div className={[
          "whitespace-pre-wrap text-sm text-[var(--ots-text-muted)]",
          density === "compact" ? "mt-1.5 leading-5" : "mt-2 leading-6",
        ].join(" ")}>{item.body}</div>
      </div>
    </li>)}
  </ol>;
};
